import { useState, useCallback, useRef } from 'react';

const MAX_HISTORY = 50;

export default function useAnnotationHistory(initialAnnotations = []) {
  const [annotations, setAnnotations] = useState(initialAnnotations);
  const historyRef = useRef([initialAnnotations]);
  const indexRef = useRef(0);

  const push = useCallback((next) => {
    const arr = typeof next === 'function' ? next(historyRef.current[indexRef.current]) : next;
    const newIndex = indexRef.current + 1;
    const newHistory = historyRef.current.slice(0, newIndex);
    newHistory.push(arr);
    if (newHistory.length > MAX_HISTORY) newHistory.shift();
    historyRef.current = newHistory;
    indexRef.current = Math.min(newIndex, newHistory.length - 1);
    setAnnotations(arr);
    return arr;
  }, []);

  const undo = useCallback(() => {
    if (indexRef.current > 0) {
      indexRef.current -= 1;
      const prev = historyRef.current[indexRef.current];
      setAnnotations(prev);
      return prev;
    }
    return null;
  }, []);

  const redo = useCallback(() => {
    if (indexRef.current < historyRef.current.length - 1) {
      indexRef.current += 1;
      const next = historyRef.current[indexRef.current];
      setAnnotations(next);
      return next;
    }
    return null;
  }, []);

  const canUndo = indexRef.current > 0;
  const canRedo = indexRef.current < historyRef.current.length - 1;

  const reset = useCallback((initial = []) => {
    historyRef.current = [initial];
    indexRef.current = 0;
    setAnnotations(initial);
  }, []);

  return { annotations, setAnnotations: push, undo, redo, canUndo, canRedo, reset };
}
